<template>
  <lf-input
    ref="searchInput"
    class="z-1 flex h-10 font-medium"
    :class="{
      'pl-4': !noPadding,
    }"
    :placeholder="placeholder"
    :value="modelValue"
    :name="uniqueName"
    type="text"
    autocomplete="off"
    form=""
    :clear-button-enabled="clearButtonEnabled"
    :dark-theme="darkTheme"
    :no-margin="noMargin"
    :disabled="disabled"
    :prefixed-slot-class="prefixedSlotClass"
    data-cy="search-input-wrapper"
    @keyup="emitValueChange"
    @clear-input="clearField"
    @focus="emit('input:focus')"
    @blur="emit('input:blur')"
  >
    <template v-if="searchType !== SearchType.AiAgent && searchIcon" #icon>
      <icon-base
        data-cy="search-icon"
        width="19"
        height="18"
        :icon="searchIcon"
      />
    </template>
    <template #prefixes>
      <slot name="prefixes" />
    </template>
  </lf-input>
</template>
<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from "vue";

import debounce from "lodash/debounce";

import { SearchType } from "@/enums/search";
import { makeRandomString } from "@/helpers/formatting";

import IconAiSearch from "@/components/icons/IconAiSearch.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

const {
  modelValue = "",
  searchTerm = "",
  clearButtonEnabled = true,
  darkTheme = false,
  noPadding = false,
  delay = 600,
  noMargin = false,
  name,
  trimValue = true,
  disabled = false,
  searchType = SearchType.Regular,
  prefixedSlotClass,
  focusOnMount = false,
} = defineProps<{
  modelValue?: string;
  searchTerm?: string;
  clearButtonEnabled?: boolean;
  darkTheme?: boolean;
  noPadding?: boolean;
  delay?: number;
  noMargin?: boolean;
  name?: string;
  trimValue?: boolean;
  disabled?: boolean;
  searchType?: SearchType;
  prefixedSlotClass?: string;
  focusOnMount?: boolean;
}>();

const searchInput = useTemplateRef<{
  inputRef: HTMLInputElement | undefined;
}>("searchInput");

const uniqueName = computed(() => name || `search-${makeRandomString(8)}`);

const emit = defineEmits<{
  "update:modelValue": [string];
  "input:focus": [];
  "input:blur": [];
}>();

const placeholder = computed(() => `Search ${searchTerm}`);

const searchIcon = computed(() => {
  if (searchType === SearchType.AiAgent) {
    return IconAiSearch;
  }

  if (searchType === SearchType.Regular) {
    return IconSearch;
  }

  return null;
});

const clearField = () => emit("update:modelValue", "");

const emitValueChange = debounce((e: Event) => {
  const { value } = e.target as HTMLInputElement;
  emit("update:modelValue", trimValue ? value.trim() : value);
}, delay);

onMounted(() => {
  if (focusOnMount && searchInput.value?.inputRef) {
    searchInput.value.inputRef.focus();
  }
});
</script>
